import React, { useState } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  LineController,
  BarController,
  ChartType
} from 'chart.js'
import { Chart } from 'react-chartjs-2'
import { Button, Paper, Box } from '@mui/material'
import { typographyStyles } from '../styles/typography'
import { useSelector } from 'react-redux'
import { State } from '../types/state'
import {
  getLastSixMonthsNames,
  getLastSixWeeks,
  getLastSevenDays,
  getLastSixYears
} from '../utils/statistics'
import { useIsMobileView } from '../hooks/useIsMobileView'
import { modernCard } from '../styles/modern'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'

ChartJS.register(
  LinearScale,
  CategoryScale,
  BarElement,
  BarController,
  PointElement,
  LineElement,
  LineController,
  Legend,
  Tooltip
)

export const options = {
  responsive: true,
  plugins: {
    legend: {
      position: 'top' as const
    }
  }
}

const getChartData = (labels: string[], data: number[]): any => ({
  labels,
  datasets: [
    {
      label: 'number of generated receipts',
      data,
      borderColor: 'rgb(255, 99, 132)',
      backgroundColor: 'rgba(255, 99, 132, 0.5)',
      fill: true
    }
  ]
})

const CHART_TYPES: ChartType[] = ['line', 'bar']

const Statistics = (): JSX.Element => {
  const isMobileView = useIsMobileView()
  const {
    daysSummary,
    weeksSummary,
    monthsSummary,
    yearsSummary,
    allTimeNumber
  } = useSelector((state: State) => state.user.receiptsCountSummary)
  const [typeIndex, setTypeIndex] = useState(0)

  const daysChartData = getChartData(getLastSevenDays(), daysSummary)
  const weeksChartData = getChartData(getLastSixWeeks(), weeksSummary)
  const monthsChartData = getChartData(getLastSixMonthsNames(), monthsSummary)
  const yearsChartData = getChartData(getLastSixYears(), yearsSummary)

  return (
    <div
      style={{
        width: isMobileView ? '80vw' : '70vw',
        margin: 'auto',
        marginBottom: '3vw',
        padding: isMobileView ? '20px' : '40px'
      }}
    >
      <Paper
        elevation={0}
        style={{
          ...modernCard,
          marginBottom: '30px',
          backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          textAlign: 'center'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          <TrendingUpIcon sx={{ fontSize: 40 }} />
          <h1 style={{ ...typographyStyles.h1White, margin: 0 }}>
            Total Receipts: {allTimeNumber}
          </h1>
        </Box>
      </Paper>

      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Button
          variant='contained'
          style={{
            marginTop: '10px',
            padding: '12px 32px',
            fontSize: '1rem',
            fontWeight: 600,
            borderRadius: '12px',
            textTransform: 'none',
            backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
          }}
          onClick={() => setTypeIndex(prev => (prev + 1) % CHART_TYPES.length)}
        >
          Change Chart Type
        </Button>
      </Box>

      <Paper elevation={0} style={{ ...modernCard, marginBottom: '30px', padding: '32px' }} className='modern-card'>
        <h2 style={{ ...typographyStyles.h2, marginBottom: '24px', textAlign: 'center' }}>
          Daily Generated Receipts
        </h2>
        <Chart
          options={options}
          data={daysChartData}
          type={CHART_TYPES[typeIndex]}
        />
      </Paper>

      <Paper elevation={0} style={{ ...modernCard, marginBottom: '30px', padding: '32px' }} className='modern-card'>
        <h2 style={{ ...typographyStyles.h2, marginBottom: '24px', textAlign: 'center' }}>
          Weekly Generated Receipts
        </h2>
        <Chart
          options={options}
          data={weeksChartData}
          type={CHART_TYPES[typeIndex]}
        />
      </Paper>

      <Paper elevation={0} style={{ ...modernCard, marginBottom: '30px', padding: '32px' }} className='modern-card'>
        <h2 style={{ ...typographyStyles.h2, marginBottom: '24px', textAlign: 'center' }}>
          Monthly Generated Receipts
        </h2>
        <Chart
          options={options}
          data={monthsChartData}
          type={CHART_TYPES[typeIndex]}
        />
      </Paper>

      <Paper elevation={0} style={{ ...modernCard, marginBottom: '30px', padding: '32px' }} className='modern-card'>
        <h2 style={{ ...typographyStyles.h2, marginBottom: '24px', textAlign: 'center' }}>
          Yearly Generated Receipts
        </h2>
        <Chart
          options={options}
          data={yearsChartData}
          type={CHART_TYPES[typeIndex]}
        />
      </Paper>
    </div>
  )
}

export default Statistics
